import {
    Avatar,
    AvatarFallback,
    AvatarImage,
    Text
} from "@solomon-labs/design/components/ui";
import React from "react";

import CHEV_DOWN_ICON from "../../assets/icons/chevron/source/chev-down.svg";

import { CutFrame } from "./CutFrame";

type TokenChipSize = "sm" | "md";

interface TokenChipProps {
    /** Token icon URL or imported asset path. */
    icon: string;
    /** Token symbol displayed next to the icon (e.g., "USDC", "USDv"). */
    symbol: string;
    /** Render the icon as a circle (used for stablecoins like USDC). */
    rounded?: boolean;
    /** Show a chevron-down indicating the chip is a token selector. */
    selectable?: boolean;
    /** Wrap the chip in a CutFrame with the bottom-right cut corner. Defaults to true. */
    framed?: boolean;
    /** Type scale for the symbol. Defaults to `body`. */
    textVariant?: "body" | "small";
    /** Icon size. `md` (default) is 20px (`size-5`), `sm` is 16px (`size-4`). */
    size?: TokenChipSize;
    /** Optional override for the chevron-down icon. Defaults to the bundled chevron asset. */
    chevronIcon?: string;
    /** Optional className passed to the outer wrapper. */
    className?: string;
}

/**
 * @version 2.1.0
 * @published 2026-04-09
 * @history
 *   2.1.0 — Icon renders through Avatar so a logo that fails to load (e.g. a
 *           rate-limited IPFS gateway) falls back to the symbol's initial
 *           instead of a broken image.
 *   2.0.0 — Added `framed` prop (default true). TokenChip now renders inside
 *           a CutFrame by default. Pass `framed={false}` for the bare version.
 *   1.0.0 — Initial publish. Token icon + symbol + optional chevron.
 */
export function TokenChip({
    icon,
    symbol,
    rounded = false,
    selectable = false,
    framed = true,
    size = "md",
    textVariant = "body",
    chevronIcon = CHEV_DOWN_ICON,
    className
}: TokenChipProps): React.ReactElement {
    const iconSizeClass = size === "sm" ? "size-4" : "size-5";

    const chip = (
        <div
            className={`flex gap-1 items-center ${framed ? "px-2 md:px-3 py-1 md:py-2" : ""} ${className ?? ""}`}
        >
            <Avatar
                className={`${iconSizeClass} border-0 bg-transparent ${rounded ? "rounded-full overflow-hidden" : ""}`}
            >
                {icon && <AvatarImage src={icon} alt={symbol} />}
                <AvatarFallback className="border border-line-base">
                    {symbol.charAt(0)}
                </AvatarFallback>
            </Avatar>
            <Text
                variant={textVariant}
                render={<span />}
                className="text-foreground-base"
            >
                {symbol}
            </Text>
            {selectable && <img src={chevronIcon} alt="" className="size-4" />}
        </div>
    );

    if (framed) {
        return (
            <CutFrame thickness={2} side="bottom-right" className="shrink-0">
                {chip}
            </CutFrame>
        );
    }

    return chip;
}
